<template>
  <v-row>
    <v-col>
      <v-card class="mb-3">
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3">
              <v-select
                label="Miqaat"
                v-model="filters.miqaatId"
                :items="miqaats"
                item-title="miqaatName"
                item-value="miqaatId"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                label="Status"
                v-model="filters.status"
                :items="REGISTRATION_STATUSES"
                item-title="title"
                item-value="value"
                clearable
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field label="ITS ID" v-model.number="filters.itsId" type="number" clearable />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-center flex-wrap">
              <v-btn class="mr-2" @click="loadRegistrations">Apply Filters</v-btn>
              <v-btn
                v-if="selectedMiqaat"
                color="primary"
                @click="openRegisterMember"
              >
                Register Member
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <data-table :tableData="tableData" :disablePagination="true">
        <template v-slot:[`item.status`]="{ item }">
          <v-chip :color="statusColor(item.status)" size="small">{{ formatStatus(item.status) }}</v-chip>
        </template>
        <template v-slot:[`item.Actions`]="{ item }">
          <v-btn size="small" variant="text" class="mr-2" @click="viewDetail(item)">View</v-btn>
          <v-btn
            v-if="item.status === 'PendingCentral'"
            size="small"
            color="success"
            variant="text"
            class="mr-2"
            @click="approve(item)"
          >
            Approve
          </v-btn>
          <v-btn
            v-if="item.status === 'PendingCentral'"
            size="small"
            color="error"
            variant="text"
            @click="reject(item)"
          >
            Reject
          </v-btn>
        </template>
      </data-table>
    </v-col>
  </v-row>

  <MiqaatRegistrationDetailDialog ref="dlgDetail" />
  <MiqaatRegisterMemberDialog
    v-if="selectedMiqaat"
    ref="dlgRegisterMember"
    :miqaat-id="selectedMiqaat.miqaatId"
    :registration-commitment-amount="selectedMiqaat.registrationCommitmentAmount"
    :niaz-commitment-required="selectedMiqaat.niazCommitmentRequired !== false"
    :niaz-commitment-compulsory="selectedMiqaat.niazCommitmentCompulsory !== false"
    @registered="loadRegistrations"
  />
</template>

<script setup>
import { reactive, ref, inject, onMounted, computed, watch } from "vue";
import { useRoute } from "vue-router";
import {
  getAll,
  getCentralRegistrations,
  centralApprove,
  centralReject,
  REGISTRATION_STATUSES,
} from "@/services/miqaatService";
import MiqaatRegistrationDetailDialog from "./components/MiqaatRegistrationDetailDialog.vue";
import MiqaatRegisterMemberDialog from "./components/MiqaatRegisterMemberDialog.vue";

const route = useRoute();
const loader = inject("loader");
const snackbar = inject("snackBar");
const inputDialog = inject("inputDialog");

const miqaats = ref([]);
const dlgDetail = ref(null);
const dlgRegisterMember = ref(null);

const selectedMiqaat = computed(() =>
  miqaats.value.find((m) => m.miqaatId === filters.miqaatId) || null
);

const filters = reactive({
  miqaatId: route.query.miqaatId ? Number(route.query.miqaatId) : null,
  status: null,
  organizationId: null,
  itsId: null,
});

const tableData = reactive({
  headers: [
    { title: "#", key: "sequence", sortable: false },
    { title: "ITS ID", key: "itsId", sortable: false },
    { title: "Full Name", key: "fullName", sortable: false },
    { title: "Organization", key: "organizationName", sortable: false },
    { title: "Registration Amount", key: "registrationCommitmentAmount", sortable: false },
    { title: "Niaz Amount", key: "niazCommitment", sortable: false },
    { title: "Status", key: "status", sortable: false },
    { title: "Actions", key: "Actions", sortable: false },
  ],
  data: [],
});

function formatStatus(status) {
  const map = {
    PendingIncharge: "Pending Incharge",
    PendingCentral: "Pending Central",
    Approved: "Approved",
    Rejected: "Rejected",
  };
  return map[status] || status;
}

function statusColor(status) {
  const map = {
    PendingIncharge: "warning",
    PendingCentral: "info",
    Approved: "success",
    Rejected: "error",
  };
  return map[status] || "grey";
}

async function loadMiqaats() {
  try {
    miqaats.value = await getAll();
  } catch (error) {
    snackbar.error(error);
  }
}

async function loadRegistrations() {
  loader.show();
  try {
    tableData.data = await getCentralRegistrations(filters);
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

watch(() => filters.miqaatId, async () => {
  tableData.data = [];
  await loadRegistrations();
});

async function viewDetail(item) {
  dlgDetail.value.show(item.miqaatRegistrationId);
}

function openRegisterMember() {
  dlgRegisterMember.value?.show();
}

async function approve(item) {
  loader.show();
  try {
    await centralApprove(item.miqaatRegistrationId);
    snackbar.success("Registration approved");
    await loadRegistrations();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

async function reject(item) {
  const answer = await inputDialog.input("Reject Registration", "Rejection reason (optional)");
  if (answer == null) return;

  loader.show();
  try {
    await centralReject(item.miqaatRegistrationId, { rejectionReason: answer.answer });
    snackbar.success("Registration rejected");
    await loadRegistrations();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

onMounted(async () => {
  await loadMiqaats();
  await loadRegistrations();
});
</script>
